import i18next from 'i18next';
import common from '../common';
import Text from './Text';

export default class Dialog extends PIXI.Container {
	/**
     * Creates a dialog with given width and height, and its destroy callback
     * @param width             {Number}            The width of the dialog
     * @param height            {Number}            The height of a dialog. Set to -1 to span to text.
     * @param destroyCB         {Object}            The destroy callback.
     * @param destroyCB._func                       The function to call onDestroy
     * @param destroyCB._scope                      The scope of the callback function
     */
	constructor(width, height, destroyCB) {
		super();

		this._minWidth = width + 100;
		this._width = width + 100;
		this._height = height || null;
		this._panel = null;
		this._destroyCallback = destroyCB || null;

		this._closeButton = null;

		this.interactive = true;

		this.on('pointertap', () => {
			this.parent.addChild(this);		// reset to top
		});

		this._init();
	}

	_init() {
		this._panel = new PIXI.Container();
		this._panel.interactive = true;
		this.addChild(this._panel);

		this._panel.body = new PIXI.NineSlicePlane(P3.assets.texture('panel_popup_9slice'), 50, 90, 50, 50);
		this._panel.body.width = this._width;
		if (this._height !== -1) {
			this._panel.body.height = this._height;
		}
		this._panel.addChild(this._panel.body);

		this._panel.titleLabel = new Text('', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });
		this._panel.titleLabel.position.set(50, 45);
		this._panel.addChild(this._panel.titleLabel);

		this._panel.body.text = new Text('', { font: '30px', align: 'left', tint: 0x0, maxWidth: this._width - 100 });
		this._panel.body.text.position.set(50, 100);
		this._panel.body.addChild(this._panel.body.text);

		const states = new P3.ButtonStates();

		states.normal = P3.assets.texture('btn_close_out');
		states.over = P3.assets.texture('btn_close_over');

		this._closeButton = common.buttonFactory.createButton(states);
		this._closeButton.animate = false;
		this._closeButton.position.set(this._panel.body.width - this._closeButton.width + 8, 60);
		this._closeButton.signals.click.add(this.onCloseButtonClick, this);
		this._panel.body.addChild(this._closeButton);

		this._closeButton.accessibleTitle = i18next.t('accessibility.button.close');

		this.resize();
		this._panel.x = -(this._width * 0.5);
		this._panel.y = -(this._height * 0.5);
	}

	destroy() {
		super.destroy();
	}

	resize(height = -1) {
		if (this._panel.body.text.style) {
			this._panel.body.text.style.wordWrapWidth = this._width - 100;
		}

		if (this._height === -1) {
			this._panel.body.height = this._panel.body.text.y + this._panel.body.text.height + 50;
		}

		if (height !== -1) {
			this._panel.body.height = height;
		}
	}

	resizeWidthToTitle() {
		this._panel.body.width = this._width = Math.max(this._width, this._panel.titleLabel.width + 200);
		this._closeButton.position.set(this._panel.body.width - this._closeButton.width + 8, 60);

		this.resize();
		this._panel.x = -(this._width * 0.5);
	}

	/**
     * Sets the text of the body.
     * @param text {String}  The text to display
     */
	setTitleText(text) {
		this._panel.titleLabel.text = text;

		this.resizeWidthToTitle();
	}

	/**
     * Sets the text of the body.
     * @param text {String} The text to display
     */
	setBodyText(text) {
		this._panel.body.text.text = text;
		this.resize();
	}

	onCloseButtonClick() {
		new TWEEN.Tween(this, this.tweens) // this tween is just here mainly because clicking on the close button will click a tile underneath. this prevents that.
			.to({
				alpha: 0,
			}, 100)
			.easing(TWEEN.Easing.Linear.None)
			.onComplete(() => {
				this.destroy();
				this._destroyCallback && this._destroyCallback._func.call(this._destroyCallback._scope);
				common.popupOpen = false;
			})
			.start();
	}

	get closeButton() {
		return this._closeButton;
	}

	get body() {
		return this._panel.body;
	}

	get panel() {
		return this._panel;
	}

	set panel(panel) {
		this._panel = panel;
	}

	get titleBar() {
		return this._panel.titleBar;
	}
}
